import React from 'react';
import { motion } from 'framer-motion';
import { GlassCard } from '../ui/GlassCard';
// IQVIA-style horizontal dot plot
// Categories on Y-axis, 1–5 strength score on X-axis, multiple competitor series each with its own marker shape & line.
type Series = {
  name: string;
  color: string;
  shape: 'circle' | 'triangle' | 'diamond';
  values: number[]; // length must match categories
};
const CATEGORIES = ['Translational biology insight', 'Imaging expertise', 'Integrated development platform', 'GMP manufacturing', 'Global infrastructure / scale'];
// Made-up illustrative data — Minerva is the protagonist
const ACTUAL: Series[] = [{
  name: 'Minerva',
  color: '#0085bd',
  shape: 'circle',
  values: [4, 4, 5, 3, 2]
}, {
  name: 'SOFIE',
  color: '#f59e0b',
  shape: 'triangle',
  values: [2, 2, 3, 5, 5]
}, {
  name: 'Radiomedix',
  color: '#10b981',
  shape: 'diamond',
  values: [3, 3, 2, 4, 3]
}];
const PERCEIVED: Series[] = [{
  name: 'Minerva',
  color: '#0085bd',
  shape: 'circle',
  values: [3, 3, 3, 3, 2]
}, {
  name: 'SOFIE',
  color: '#f59e0b',
  shape: 'triangle',
  values: [5, 3, 3, 5, 5]
}, {
  name: 'Radiomedix',
  color: '#10b981',
  shape: 'diamond',
  values: [4, 3, 2, 4, 3]
}];
function Marker({
  shape,
  color,
  size = 8
}: {
  shape: Series['shape'];
  color: string;
  size?: number;
}) {
  if (shape === 'circle') {
    return <circle r={size} fill={color} stroke="white" strokeWidth="1.5" data-id="element-457" />;
  }
  if (shape === 'triangle') {
    const s = size * 1.2;
    return <polygon points={`0,${-s} ${s * 0.9},${s * 0.6} ${-s * 0.9},${s * 0.6}`} fill={color} stroke="white" strokeWidth="1.5" data-id="element-458" />;
  }
  // diamond
  const s = size * 1.1;
  return <polygon points={`0,${-s} ${s},0 0,${s} ${-s},0`} fill={color} stroke="white" strokeWidth="1.5" data-id="element-459" />;
}
function DotPlot({
  title,
  series,
  delayBase
}: {
  title: string;
  series: Series[];
  delayBase: number;
}) {
  // Layout constants (in viewBox coordinates)
  const VB_W = 560;
  const VB_H = 340;
  const PAD_L = 220;
  const PAD_R = 30;
  const PAD_T = 30;
  const PAD_B = 60;
  const plotW = VB_W - PAD_L - PAD_R;
  const plotH = VB_H - PAD_T - PAD_B;
  const rowH = plotH / CATEGORIES.length;
  const xMin = 1,
    xMax = 5;
  const xFor = (v: number) => PAD_L + (v - xMin) / (xMax - xMin) * plotW;
  const yFor = (i: number) => PAD_T + i * rowH + rowH / 2;
  return <div className="flex-1 flex flex-col" data-id="element-460">
      <div className="text-xs font-mono uppercase tracking-widest text-snakker-muted mb-2" data-id="element-461">
        {title}
      </div>

      <div className="flex-1 rounded-xl border border-snakker-border bg-white p-3" data-id="element-462">
        <svg viewBox={`0 0 ${VB_W} ${VB_H}`} className="w-full h-full" data-id="element-463">
          {/* Alternating row bands */}
          {CATEGORIES.map((_, i) => <rect key={i} x={PAD_L} y={PAD_T + i * rowH} width={plotW} height={rowH} fill={i % 2 === 0 ? '#f5f5f7' : 'transparent'} data-id="element-464" />)}

          {/* Vertical grid + x-axis ticks (1..5) */}
          {[1, 2, 3, 4, 5].map(v => <g key={v} data-id="element-465">
              <line x1={xFor(v)} x2={xFor(v)} y1={PAD_T} y2={PAD_T + plotH} stroke="rgba(13,15,20,0.06)" strokeWidth="1" data-id="element-466" />
              <text x={xFor(v)} y={PAD_T + plotH + 18} textAnchor="middle" fontSize="11" fill="rgba(13,15,20,0.5)" fontFamily="ui-monospace, monospace" data-id="element-467">
                {v}
              </text>
            </g>)}

          {/* Y-axis category labels */}
          {CATEGORIES.map((cat, i) => <text key={cat} x={PAD_L - 10} y={yFor(i) + 4} textAnchor="end" fontSize="11" fill="#0d0f14" fontWeight="500" data-id="element-468">
              {cat}
            </text>)}

          {/* X-axis label */}
          <text x={PAD_L + plotW / 2} y={VB_H - 12} textAnchor="middle" fontSize="10" fill="rgba(13,15,20,0.55)" fontFamily="ui-monospace, monospace" data-id="element-469">
            Strength score (1 = weaker → 5 = stronger)
          </text>

          {/* Series: connecting line + markers */}
          {series.map((s, sIdx) => {
          const points = s.values.map((v, i) => `${xFor(v)},${yFor(i)}`).join(' ');
          return <g key={s.name} data-id="element-470">
                <motion.polyline points={points} fill="none" stroke={s.color} strokeWidth="1.8" strokeOpacity="0.9" initial={{
              pathLength: 0,
              opacity: 0
            }} animate={{
              pathLength: 1,
              opacity: 1
            }} transition={{
              duration: 1.2,
              delay: delayBase + sIdx * 0.25,
              ease: 'easeOut'
            }} data-id="element-471" />
                {s.values.map((v, i) => <motion.g key={i} transform={`translate(${xFor(v)}, ${yFor(i)})`} initial={{
              opacity: 0,
              scale: 0
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              delay: delayBase + sIdx * 0.25 + i * 0.07 + 0.4,
              type: 'spring',
              stiffness: 300
            }} data-id="element-472">
                    <Marker shape={s.shape} color={s.color} data-id="element-473" />
                  </motion.g>)}
              </g>;
        })}
        </svg>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-5 pt-3" data-id="element-474">
        {series.map(s => <div key={s.name} className="flex items-center gap-1.5 text-xs text-snakker-muted" data-id="element-475">
            <svg width="16" height="16" viewBox="-8 -8 16 16" data-id="element-476">
              <Marker shape={s.shape} color={s.color} size={6} data-id="element-477" />
            </svg>
            <span className="font-medium text-snakker-ink" data-id="element-478">{s.name}</span>
          </div>)}
      </div>
    </div>;
}
export function Slide16Capability() {
  return <div className="w-full h-full flex flex-col max-w-[95rem] mx-auto pt-8" data-id="element-479">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="mb-4" data-id="element-480">
        <div className="inline-block px-3 py-1 rounded-full bg-snakker-secondary/10 border border-snakker-secondary/20 text-xs font-mono uppercase tracking-widest text-snakker-secondary mb-3" data-id="element-481">
          Strategic Market Intelligence
        </div>
        <h2 className="text-[clamp(2rem,3.4vw,3.4rem)] font-light tracking-tight text-snakker-ink leading-tight" data-id="element-482">
          Capability reality vs{' '}
          <span className="text-gradient-accent font-medium" data-id="element-483">
            market perception.
          </span>
        </h2>
      </motion.div>

      {/* Two-panel comparison */}
      <motion.div className="flex-1 flex gap-6 min-h-0" initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.3
    }} data-id="element-484">
        <DotPlot title="A · Actual capabilities" series={ACTUAL} delayBase={0.5} data-id="element-485" />
        <DotPlot title="B · Market perception" series={PERCEIVED} delayBase={1.4} data-id="element-486" />
      </motion.div>

      {/* Insight strip */}
      <motion.div className="mt-5 grid grid-cols-3 gap-4" initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 2.2
    }} data-id="element-487">
        <div className="p-4 rounded-xl border border-snakker-border bg-white" data-id="element-488">
          <div className="text-[10px] font-mono uppercase tracking-widest text-snakker-subtle mb-1" data-id="element-489">
            The market sees
          </div>
          <div className="text-sm text-snakker-ink leading-snug" data-id="element-490">
            Large players as <span className="font-medium" data-id="element-491">safer</span>
          </div>
        </div>
        <div className="p-4 rounded-xl border border-snakker-border bg-white" data-id="element-492">
          <div className="text-[10px] font-mono uppercase tracking-widest text-snakker-subtle mb-1" data-id="element-493">
            And
          </div>
          <div className="text-sm text-snakker-ink leading-snug" data-id="element-494">
            Smaller specialists as{' '}
            <span className="font-medium" data-id="element-495">similar to each other</span>
          </div>
        </div>
        <div className="p-4 rounded-xl border border-snakker-secondary/30 bg-snakker-secondary/[0.06]" data-id="element-496">
          <div className="text-[10px] font-mono uppercase tracking-widest text-snakker-secondary mb-1" data-id="element-497">
            Implication
          </div>
          <div className="text-sm text-snakker-ink leading-snug" data-id="element-498">
            Minerva's scientific strengths are{' '}
            <span className="font-medium text-snakker-secondary" data-id="element-499">
              not fully recognized.
            </span>
          </div>
        </div>
      </motion.div>
    </div>;
}